/**
 * Saferfunds API Client
 * Centralises all calls to the Flask backend at api.saferfunds.com
 * In dev the Vite proxy rewrites /api → backend; in prod VITE_API_BASE_URL is set.
 */

const BASE = import.meta.env.VITE_API_BASE_URL || ''

function authHeaders(extra = {}) {
  const token = localStorage.getItem('sf_auth_token')
  return {
    ...extra,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }
}

async function get(path) {
  const res = await fetch(`${BASE}${path}`, { headers: authHeaders() })
  if (res.status === 401) {
    // Token expired — clear it so ProtectedRoute redirects to /login
    localStorage.removeItem('sf_auth_token')
    window.location.href = '/login'
    throw new Error('Session expired')
  }
  if (!res.ok) throw new Error(`GET ${path} → ${res.status}`)
  return res.json()
}

async function post(path, body) {
  const res = await fetch(`${BASE}${path}`, {
    method: 'POST',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(body),
  })
  if (res.status === 401) {
    localStorage.removeItem('sf_auth_token')
    window.location.href = '/login'
    throw new Error('Session expired')
  }
  if (!res.ok) throw new Error(`POST ${path} → ${res.status}`)
  return res.json()
}

/** Raw SQL query via /api/db/access. Returns { success, data: [[...]] } */
export async function dbQuery(sql) {
  return post('/api/db/access', { query: sql })
}

export const api = {
  maintenance:      { run: () => post('/api/maintenance/run', {}), status: () => get('/api/maintenance/status') },
  health:           () => get('/api/health'),
  metrics:          () => get('/api/metrics'),
  signalsRecent:    () => get('/api/signals/recent'),
  marketOverview:   () => get('/api/market/overview'),
  dbMetrics:        () => get('/api/metrics/database'),

  // Dashboard upgrade endpoints (2026-03-10)
  // 2026-09-19: MAIN and Tier 2 are both genuinely live, separate Alpaca (2026-11-03: a third, JoshSkills, is outside the engine and not served here)
  // accounts (see docs/GOAL_RECOVER_TO_100K.md) -- callers should pass an
  // explicit account rather than rely on this default. The 'tier2' default
  // here is a historical artifact from 2026-09-09, when MAIN was believed
  // retired; every caller in this codebase now passes `selectedAccount`
  // (AccountContext's header switcher) or 'main' explicitly.
  account:          (account = 'tier2') => get(`/api/account?account=${encodeURIComponent(account)}`),
  portfolioHistory: (period = '1M', account = 'tier2') =>
    get(`/api/portfolio/history?period=${period}&account=${encodeURIComponent(account)}`),
  // NOT switched to tier2 -- this endpoint's "stop price / stop distance"
  // is momentum's own Phase A/B trailing-stop math (sf_signal_scanner.py);
  // Tier 2 has no trailing stop at all (SIGNAL/TIMEBOX exits only), so
  // pointing this at Tier 2 positions would show a fabricated stop price
  // for a mechanism that doesn't govern them. Needs a real Tier-2-shaped
  // replacement (time-to-TIMEBOX, distance-to-SIGNAL) -- built 2026-09-09
  // as tier2Positions/tier2Activity below; Portfolio.jsx uses those now.
  livePnl:          () => get('/api/positions/live-pnl'),
  trades:           (limit = 100, account = 'tier2') =>
    get(`/api/trades?limit=${encodeURIComponent(limit)}&account=${encodeURIComponent(account)}`),
  performance:      (days = 30) => get(`/api/performance?days=${days}`),

  // Tier 2 real-data replacements for Portfolio's old momentum-only panels
  // (2026-09-09) -- see /api/tier2/positions and /api/tier2/activity in
  // app.py for the full rationale.
  tier2Positions:   () => get('/api/tier2/positions'),
  mainPositions:    () => get('/api/main/positions'),   // MAIN account positions + stop status (2026-10-04)
  tier2Activity:    (days = 7, limit = 50) => get(`/api/tier2/activity?days=${days}&limit=${limit}`),

  // Watchlist management (2026-03-10)
  watchlist:        ()                         => get('/api/watchlist'),
  watchlists:       ()                         => get('/api/watchlists'),
  createWatchlist:  (name, description)        => post('/api/watchlists', { name, description }),
  validateSymbol:   (symbol)                   => get(`/api/watchlist/validate/${symbol.toUpperCase()}`),
  screener:         (q, exchange)              => get(`/api/watchlist/screener?q=${encodeURIComponent(q)}${exchange ? `&exchange=${exchange}` : ''}`),
  addSymbol:        (symbol, sector, watchlist) => post('/api/watchlist/add', { symbol, sector, watchlist }),
  bulkAdd:          (symbols)                  => post('/api/watchlist/bulk-add', { symbols }),
  removeSymbol:     (symbol)                   => post('/api/watchlist/remove', { symbol }),
  validateAll:      ()                         => post('/api/watchlist/validate-all', {}),
  fetchTradingView: (url)                      => post('/api/watchlist/fetch-tradingview', { url }),
  syncTradingView:  (symbols, disableMissing, sectorMap) =>
    post('/api/watchlist/sync-tradingview', {
      symbols,
      disable_missing: disableMissing,
      sector_map: sectorMap || {},
    }),
  detectSectors:    (symbols)                 => post('/api/watchlist/detect-sectors', { symbols }),

  /** Recent bullish signals on monitoring-only (trade_enabled=false) symbols --
   *  these never reach the broker, surfaced here for manual "should this be
   *  trading?" review. */
  monitoringAlerts: (hours = 24, limit = 100) =>
    get(`/api/watchlist/monitoring-alerts?hours=${encodeURIComponent(hours)}&limit=${encodeURIComponent(limit)}`),

  /** Enable/disable live trading for a symbol without removing it from the
   *  watchlist -- data collection and signal generation keep running either way. */
  setTradeEnabled:  (symbol, tradeEnabled)    => post('/api/watchlist/set-trade-enabled', { symbol, trade_enabled: tradeEnabled }),

  /** Fetch signals from the live active_signals table.
   *  Uses a parameterized server-side endpoint — no SQL in the frontend. */
  signals: (hours = 24, limit = 100) =>
    get(`/api/signals/list?hours=${encodeURIComponent(hours)}&limit=${encodeURIComponent(limit)}`),

  /** Fetch recent gate decisions from logic_gate_decisions. */
  decisions: (days = 7, limit = 50) =>
    get(`/api/decisions/list?days=${encodeURIComponent(days)}&limit=${encodeURIComponent(limit)}`),

  /** Recent 1-min bars for a single symbol.
   *  Symbol is validated server-side against [A-Z0-9.-]{1,10}. */
  symbolBars: (symbol, limit = 60) =>
    get(`/api/bars/${encodeURIComponent(symbol.trim().toUpperCase())}?limit=${encodeURIComponent(limit)}`),

  /** Signal type breakdown grouped by type and direction. */
  signalBreakdown: (hours = 24) =>
    get(`/api/signals/breakdown?hours=${encodeURIComponent(hours)}`),

  /** Top symbols by signal count for the last N hours. */
  topSymbols: (hours = 24, limit = 10) =>
    get(`/api/signals/top-symbols?hours=${encodeURIComponent(hours)}&limit=${encodeURIComponent(limit)}`),

  /** Market regime distribution from the last hour of realtime bars. */
  marketRegime: () => get('/api/market/regime'),
}

// ── Schwab API credential + auth helpers ───────────────────────────────────────
export const schwab = {
  /** Current auth status: credentials_configured, token_valid, token_expires_at, data_source */
  status:      () => get('/api/schwab/status'),

  /** Whether credentials are saved (never returns the actual keys) */
  credentials: () => get('/api/schwab/credentials'),

  /**
   * Save Schwab app key + secret to the backend DB.
   * @param {string} app_key
   * @param {string} app_secret
   * @param {string} redirect_uri
   */
  saveCredentials: (app_key, app_secret, redirect_uri) =>
    post('/api/schwab/credentials', { app_key, app_secret, redirect_uri }),

  /** Get the OAuth URL to open in a browser popup for user authorization */
  authUrl: () => get('/api/schwab/auth-url'),

  /** Combined health for both Schwab and Alpaca */
  multiHealth: () => get('/api/multi-api/health'),

  /** Live API test: fetches SPY quote + returns token created/expiry metadata */
  test: () => get('/api/schwab/test'),

  /**
   * Delete the stored OAuth token from the DB, forcing a full re-authorization.
   * Use when switching apps or after adding 'Accounts and Trading' to an existing
   * app — the old refresh token will not inherit the new API scope.
   */
  revoke: () => post('/api/schwab/revoke', {}),
}

// ── Data source control ────────────────────────────────────────────────────────
export const dataSource = {
  /** Current active source ('schwab'|'alpaca') + streaming stats */
  get:    ()         => get('/api/config/data-source'),
  /** Switch active source. source = 'schwab' | 'alpaca' */
  set:    (source)   => post('/api/config/data-source', { source }),
}

// ── System config (live values from backend constants) ─────────────────────────
export const systemConfig = {
  get: () => get('/api/config/system'),
  /** Update runtime values. body = { max_positions?, trading_enabled? } */
  set: (body) => post('/api/config/system', body),
}

// ── Logic gates (thresholds + 24h pass-rate stats) ──────────────────────────────
export const gates = {
  get:  ()        => get('/api/config/gates'),
  /** body = { [gate_key]: newThreshold, ... } */
  save: (body)    => post('/api/config/gates', body),
}

// ── Streaming/WebSocket health (no auth required by the backend, but the shared
// client's auth header is harmless to send either way) ─────────────────────────
export const streaming = {
  status: () => get('/api/streaming/status'),
}

// ── Stock screener (Discovery page) ───────────────────────────────────────────
export const screener = {
  /** stored candidates for a scan date (defaults to the latest) */
  candidates: (params = {}) => {
    const q = new URLSearchParams(params).toString()
    return get(`/api/screener/candidates${q ? `?${q}` : ''}`)
  },
  /** full gate/score breakdown + chart slice for one symbol */
  candidate: (symbol) => get(`/api/screener/candidate/${encodeURIComponent(symbol)}`),
  /** re-run the gate/score over stored factors at an arbitrary config (no persist) */
  preview: (config, date) => post('/api/screener/preview', { config, date }),
  /** descriptors + presets for the controls panel */
  config: () => get('/api/screener/config'),
  /** save the current controls as custom_<name> */
  saveConfig: (name, config) => post('/api/screener/config', { name, config }),
  /** which config the nightly scan runs on */
  settings: () => get('/api/screener/settings'),
  setNightlyConfig: (nightly_config_name, by) =>
    fetch(`${BASE}/api/screener/settings`, {
      method: 'PUT',
      headers: authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ nightly_config_name, by }),
    }).then((r) => r.json()),
  /** re-run gate/score over recent stored factors under the current nightly config */
  rescore: (days = 20) => post('/api/screener/rescore', { days }),
  /** last scan runs + universe size */
  status: () => get('/api/screener/status'),
  /** one-click promote → reuses the watchlist add path */
  promote: (symbol, by) => post('/api/screener/promote', { symbol, by }),
  ignore: (symbol, weeks) => post('/api/screener/ignore', { symbol, weeks }),
}

// ── Dashboard notepad (2026-10-04): notes saved on the server, tagged with interface version and page ──
export const notes = {
  list:   ()                       => get('/api/notes'),
  add:    (body, version, page)    => post('/api/notes', { body, version, page }),
  done:   (id, done)               => post(`/api/notes/${id}/done`, { done }),
  remove: (id)                     => post(`/api/notes/${id}/delete`, {}),
}

// ── Pre-open readiness check (2026-10-05): will MAIN and Tier 2 place orders at the open? Read-only. ──
export const preopen = {
  run: () => get('/api/system/preopen-check'),
}

// ── Watchers page (T-132): every watcher, worker, agent and cron ──
// T-342 Model Desk: snapshot written by the host job scripts/ops/delegation/model_desk_snapshot.py; verdicts are queued, applied by that job.
export const modelDesk = {
  overview: ()        => get('/api/models/overview'),
  verdict:  (body)    => post('/api/models/verdict', body),
}

// T-381 Research > Tools: latest result row a host tool job wrote (tool_results); read-only.
export const tools = {
  result: (tool) => get(`/api/tools/${tool}`),
}

export const watchers = {
  list:    ()            => get('/api/watchers'),
  running: ()            => get('/api/watchers/running'),   // T-254: what is running now + ETA (header indicator, Watchers banner)
  log:     (id)          => get(`/api/watchers/${id}/log`),
  pause:   (id, minutes) => post(`/api/watchers/${id}/pause`, { minutes }),
  resume:  (id)          => post(`/api/watchers/${id}/pause`, { resume: true }),
  run:     (id, confirm) => post(`/api/watchers/${id}/run`, { confirm }),
  request: (id, body)    => post(`/api/watchers/${id}/request`, { body }),
}

// ── System alerts (written by the automated monitoring cloud routines, 2026-08-28) ──
export const alerts = {
  active:  ()   => get('/api/alerts/active'),
  resolve: (id) => post(`/api/alerts/${id}/resolve`, {}),
  // T-336: CLEAR ALL for one alert type (server resolves every unresolved row of that source+type) or for everything.
  resolveType:    (source, type) => post('/api/alerts/resolve-type', { source, type }),
  resolveAll:     ()             => post('/api/alerts/resolve-type', { all: true }),
  recentResolved: ()             => get('/api/alerts/recent-resolved'),
}

// ── Reverse indicator search (Testing page, 2026-09-15) ─────────────────────────
// Runs as a background subprocess on the backend (app.py spawns
// scripts/reverse_backtest_runner.py) -- see that route's docstring in app.py
// for why it's not run in-process. run() uses a raw fetch, not the shared
// post(), for the same reason as tradeTest.run() below: a 409 ("already
// running") or 400 (bad params) still carries a useful JSON body that the
// UI wants to show, not just a generic thrown Error.
export const reverseBacktest = {
  /** params: { symbols?, days?, min_bars?, top_n?, active_only? } — all optional. */
  run: async (params = {}) => {
    const res = await fetch(`${BASE}/api/reverse-backtest/run`, {
      method: 'POST',
      headers: authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(params),
    })
    if (res.status === 401) {
      localStorage.removeItem('sf_auth_token')
      window.location.href = '/login'
      throw new Error('Session expired')
    }
    const body = await res.json()
    return { ...body, httpStatus: res.status }
  },
  /** { success, status: {state, started_at, finished_at, last_message, error, pid} | null, updated_at } */
  status:  () => get('/api/reverse-backtest/status'),
  /** { success, data: <run_search() result> | null, last_run } */
  results: () => get('/api/reverse-backtest/results'),
}

// ── Counterfactual exit replay (Testing page, 2026-10-07, T-187) ─────────────────
// POST starts a background subprocess on the backend (research only, never changes a live parameter); GET returns
// { success, status: {state: running|completed|failed, last_message, error, result}, updated_at }. run() uses a raw fetch for the
// same reason as reverseBacktest.run(): a 409 (already running) or 400 still carries a JSON body the UI wants to show.
export const exitReplay = {
  /** days: 1-90, default 30 */
  run: async (days = 30) => {
    const res = await fetch(`${BASE}/api/research/run-exit-replay`, {
      method: 'POST',
      headers: authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ days }),
    })
    if (res.status === 401) {
      localStorage.removeItem('sf_auth_token')
      window.location.href = '/login'
      throw new Error('Session expired')
    }
    const body = await res.json()
    return { ...body, httpStatus: res.status }
  },
  status: () => get('/api/research/run-exit-replay'),
}

// ── Trading Monitor (2026-10-08, T-195): Tier 2 fault monitor. state() is cheap and polled by the global fault modal. ──
async function monitorPost(path, body) {
  const res = await fetch(`${BASE}${path}`, {
    method: 'POST',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: body === undefined ? undefined : JSON.stringify(body),
  })
  if (res.status === 401) {
    localStorage.removeItem('sf_auth_token')
    window.location.href = '/login'
    throw new Error('Session expired')
  }
  return { ...(await res.json()), httpStatus: res.status }   // 400/409 carry a JSON error the UI shows
}
export const tradingMonitor = {
  /** { success, config: {enabled, interval_min}, state, unanswered: Fault[], running, queue_pending, interval_choices_min, thresholds } */
  state: () => get('/api/trading-monitor/state'),
  setConfig: (enabled, interval_min) => monitorPost('/api/trading-monitor/config', { enabled, interval_min }),
  runNow: () => monitorPost('/api/trading-monitor/run-now'),
  /** choice: 'fix_now' | 'add_todo' */
  choose: (faultId, choice) => monitorPost(`/api/trading-monitor/fault/${encodeURIComponent(faultId)}/choice`, { choice }),
}

// ── Testing card modal (2026-10-08, T-194): formula text, lookback limits and the live-book baseline statistics for one card ──
export const testingCard = {
  /** { success, id, source, formula: string[], lookback: {min, max, default, days}, stats: {...}, stats_scope } */
  get: (id, days) => get(`/api/testing/card/${encodeURIComponent(id)}${days ? `?days=${encodeURIComponent(days)}` : ''}`),
}

// ── Optimizer summary and Macro Optics (read-only, 2026-10-07) ─────────────────────
export const backtestRecent = {
  /** { success, data: build_summary() of src/backtest/optimizer.py | null, updated_at } */
  get: () => get('/api/backtest/recent'),
}

export const reconstructionStatus = {
  /** { success, tables_present, generated_at, stale_minutes, runs: [{run_name, health, status, window, last_epoch, epochs_done, calendar_progress, config, funnel, snapshots}] } */
  get: () => get('/api/testing/reconstruction'),
}

// ── Edge Diagnostics (read-only, 2026-10-14): taken vs blocked, gate impact, latency/friction, parameter health, one verdict line ──
export const edgeDiagnostics = {
  /** { success, window, sections: {taken_vs_blocked, gate_impact, latency_friction, params}, verdict, operating_rule, definitions }; opts: days, engine, horizon, includeFallback */
  get: ({ days = 5, engine = 'TIER2', horizon = '15m', includeFallback = false } = {}) =>
    get(`/api/edge/diagnostics?days=${encodeURIComponent(days)}&engine=${encodeURIComponent(engine)}&horizon=${encodeURIComponent(horizon)}&include_fallback=${includeFallback ? 1 : 0}`),
}

export const macroOptics = {
  /** { success, data: { macro_optic_sync: {...} } | null, updated_at } */
  latest: () => get('/api/macro-optics/latest'),
}

// ── Trade flow test ────────────────────────────────────────────────────────────
export const tradeTest = {
  /**
   * Run end-to-end trade flow: BUY 1 share → fill → SELL 1 share → fill.
   * Returns { success, steps: [{step, status, detail, ts}], symbol,
   *           buy_order_id, sell_order_id, fill_price, sell_price, db_age_min }
   *
   * Uses a raw fetch (not the shared post()) so that a non-2xx HTTP status still
   * returns the JSON body with step details rather than throwing a generic error.
   */
  run: async (symbol = 'SPY') => {
    const BASE = import.meta.env.VITE_API_BASE_URL || ''
    const token = localStorage.getItem('sf_auth_token')
    const res = await fetch(`${BASE}/api/test/trade-flow`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ symbol }),
    })
    if (res.status === 401) {
      localStorage.removeItem('sf_auth_token')
      window.location.href = '/login'
      throw new Error('Session expired')
    }
    // Return the JSON body even on error status — backend includes step details
    return res.json()
  },
}
